import React, { useState, useEffect, useRef } from 'react';
import SEO from '../../components/SEO';
import { useParams, useNavigate } from 'react-router-dom';
import { testsData } from '../../data/testsData';
import { saveTestProgress, loadTestProgress, clearTestProgress } from '../../utils/testStorage';
import { calculateTestResult } from '../../utils/testScoring';
import { UserResponse, TestProgress } from '../../types/tests';
import './TestTaking.css';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';

const TestTaking: React.FC = () => {
    const { testId } = useParams<{ testId: string }>();
    const navigate = useNavigate();

    const test = testsData.find((t) => t.id === testId);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [responses, setResponses] = useState<UserResponse[]>([]);
    const [selectedAnswerId, setSelectedAnswerId] = useState<string | null>(null);
    const [showValidation, setShowValidation] = useState(false);
    const hasCheckedProgress = useRef(false);

    // Modal states
    const [resumeModalOpen, setResumeModalOpen] = useState(false);
    const [exitModalOpen, setExitModalOpen] = useState(false);
    const [pendingProgress, setPendingProgress] = useState<TestProgress | null>(null);

    // Load saved progress on mount
    useEffect(() => {
        if (!test || hasCheckedProgress.current) return;

        const savedProgress = loadTestProgress(test.id);
        if (savedProgress) {
            setPendingProgress(savedProgress);
            setResumeModalOpen(true);
        }

        hasCheckedProgress.current = true;
    }, [test]);

    const handleConfirmResume = () => {
        if (!test || !pendingProgress) return;

        setCurrentQuestionIndex(pendingProgress.currentQuestionIndex);
        setResponses(pendingProgress.responses);

        // Check if there's a saved answer for current question
        const currentResponse = pendingProgress.responses.find(
            r => r.questionId === test.questions[pendingProgress.currentQuestionIndex].id
        );
        if (currentResponse) {
            setSelectedAnswerId(currentResponse.answerId);
        }
        setResumeModalOpen(false);
    };

    const handleCancelResume = () => {
        if (!test) return;
        clearTestProgress(test.id);
        setResumeModalOpen(false);
    };

    // Save progress whenever it changes
    useEffect(() => {
        if (!test || responses.length === 0) return;

        const progress: TestProgress = {
            testId: test.id,
            currentQuestionIndex,
            responses,
            startedAt: new Date().toISOString(),
            lastUpdated: new Date().toISOString(),
        };

        saveTestProgress(progress);
    }, [test, currentQuestionIndex, responses]);

    if (!test) {
        return (
            <div className="test-taking-container">
                <div className="error-message">Тест не найден</div>
                <button className="nav-button" onClick={() => navigate('/materials/tests')}>
                    Вернуться к списку
                </button>
            </div>
        );
    }

    const currentQuestion = test.questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / test.questions.length) * 100;

    const handleAnswerSelect = (answerId: string) => {
        setSelectedAnswerId(answerId);
        setShowValidation(false);
    };

    const handleNext = () => {
        if (!selectedAnswerId) {
            setShowValidation(true);
            return;
        }

        const selectedAnswer = currentQuestion.answers.find(a => a.id === selectedAnswerId);
        if (!selectedAnswer) return;

        // Update responses
        const newResponses = [...responses.filter(r => r.questionId !== currentQuestion.id)];
        newResponses.push({
            questionId: currentQuestion.id,
            answerId: selectedAnswerId,
            score: selectedAnswer.score,
        });
        setResponses(newResponses);

        // Check if this was the last question
        if (currentQuestionIndex === test.questions.length - 1) {
            // Calculate total score
            const totalScore = newResponses.reduce((sum, r) => sum + r.score, 0);
            const result = calculateTestResult(test, totalScore, newResponses);

            // Clear saved progress
            clearTestProgress(test.id);

            // Navigate to results
            navigate(`/materials/test/${testId}/results`, { state: { result } });
        } else {
            // Move to next question
            setCurrentQuestionIndex(currentQuestionIndex + 1);

            // Check if next question already has an answer
            const nextQuestion = test.questions[currentQuestionIndex + 1];
            const nextResponse = newResponses.find(r => r.questionId === nextQuestion.id);
            setSelectedAnswerId(nextResponse ? nextResponse.answerId : null);
        }
    };

    const handleBack = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);

            // Load answer for previous question
            const prevQuestion = test.questions[currentQuestionIndex - 1];
            const prevResponse = responses.find(r => r.questionId === prevQuestion.id);
            setSelectedAnswerId(prevResponse ? prevResponse.answerId : null);
            setShowValidation(false);
        }
    };

    const handleExit = () => {
        setExitModalOpen(true);
    };

    const handleConfirmExit = () => {
        navigate('/materials/tests');
    };

    const handleCancelExit = () => {
        setExitModalOpen(false);
    };

    return (
        <div className="main-content-container">
            <SEO
                title={`${test.title} | Тестирование`}
                description={test.description}
                canonical={`/materials/test/${testId}`}
                noIndex={true} // Don't index the taking process
            />
            <div className="test-taking-container">
                <div className="test-header">
                    <h2 className="test-title">{test.title}</h2>
                    <button className="test-exit-button" onClick={handleExit}>
                        ✕
                    </button>
                </div>

                {(test.fullDescription || test.instruction) && (
                    <details className="test-info-details">
                        <summary className="test-info-summary">Описание и инструкция</summary>
                        <div className="test-info-content">
                            {test.fullDescription && (
                                <div className="test-description-section">
                                    <h3>Описание</h3>
                                    <p>{test.fullDescription}</p>
                                </div>
                            )}

                            {test.instruction && (
                                <div className="test-instruction-section">
                                    <h3>Инструкция</h3>
                                    <div className="instruction-text">
                                        {test.instruction.split('\n').map((line, i) => (
                                            <p key={i}>{line}</p>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </details>
                )}

                <div className="progress-bar-container">
                    <div className="progress-bar" style={{ width: `${progress}%` }} />
                </div>

                <div className="question-counter">
                    Вопрос {currentQuestionIndex + 1} из {test.questions.length}
                </div>

                <div className="question-text">{currentQuestion.text}</div>

                <div className="answers-container">
                    {currentQuestion.answers.map((answer) => (
                        <div
                            key={answer.id}
                            className={`answer-option ${selectedAnswerId === answer.id ? 'selected' : ''}`}
                            onClick={() => handleAnswerSelect(answer.id)}
                        >
                            <div className="answer-radio">
                                {selectedAnswerId === answer.id && <div className="answer-radio-dot" />}
                            </div>
                            <div className="answer-text">{answer.text}</div>
                        </div>
                    ))}
                </div>

                {showValidation && (
                    <div className="validation-message">
                        Пожалуйста, выберите ответ перед продолжением
                    </div>
                )}

                <div className="test-navigation">
                    <button
                        className="test-nav-button"
                        onClick={handleBack}
                        disabled={currentQuestionIndex === 0}
                    >
                        Назад
                    </button>
                    <button className="test-nav-button test-nav-button-primary" onClick={handleNext}>
                        {currentQuestionIndex === test.questions.length - 1 ? 'Завершить' : 'Далее'}
                    </button>
                </div>
            </div>

            <ConfirmModal
                isOpen={resumeModalOpen}
                title="Восстановление прогресса"
                message="Вы начали этот тест ранее. Хотите продолжить с того места, где остановились?"
                onConfirm={handleConfirmResume}
                onCancel={handleCancelResume}
                confirmText="Продолжить"
                cancelText="Начать заново"
            />

            <ConfirmModal
                isOpen={exitModalOpen}
                title="Прервать тест?"
                message="Вы уверены, что хотите прервать тест? Ваш прогресс будет сохранен."
                onConfirm={handleConfirmExit}
                onCancel={handleCancelExit}
                confirmText="Прервать"
                cancelText="Отмена"
            />
        </div>
    );
};

export default TestTaking;
